import { getDataApiUrl, API_CONFIG } from '../config/api';
import authService from './authService';
import { AircraftPartRequest } from '../types/aircraft';

// Интерфейсы для данных
export interface Flight {
  id: number;
  flightNumber: string;
  departureAirport: string;
  arrivalAirport: string;
  departureTime: string;
  arrivalTime: string;
  status: {
    id: number;
    code: string;
    name: string;
    displayName: string;
    color: string;
  };
  carrier: {
    id: number;
    code: string;
    name: string;
    displayName: string;
    country: string;
  };
  aircraftType: {
    id: number;
    code: string;
    name: string;
    displayName: string;
    manufacturer: string;
    capacity: number;
  };
}

export interface Car {
  id: number;
  name: string;
  model: string;
  number: number;
  imei: number;
  airport: string;
  createdAt: string;
  calibrationTablesCount: number;
  calibrationTables: any;
}

export interface Status {
  id: number;
  name: string;
  description: string;
  color: string;
  isActive: boolean;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: number;
  email: string;
  username: string;
  name: string;
  surname: string;
  patronymic: string;
  role: string;
  position: string;
  airport: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRequestData {
  title: string;
  description: string;
  flightId: number;
  carId: number;
  statusId: number;
  creatorId: number;
  responsibleId: number;
  aircraftParts: AircraftPartRequest[]; // Изменено на массив запросов частей
  priority: string;
  liquidType?: string;
}

// Общий интерфейс для пагинированных ответов
export interface PaginatedResponse<T> {
  content: T[];
  pageable: {
    pageNumber: number;
    pageSize: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    offset: number;
    paged: boolean;
    unpaged: boolean;
  };
  last: boolean;
  totalPages: number;
  totalElements: number;
  size: number;
  number: number;
  sort: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
  first: boolean;
  numberOfElements: number;
  empty: boolean;
}

class DataService {
  private getAuthHeaders(): HeadersInit {
    const token = authService.getAccessToken();
    return {
      ...API_CONFIG.HEADERS,
      'Authorization': `Bearer ${token}`
    };
  }

  // Получение статусов
  async getStatuses(page: number = 0, size: number = 20): Promise<PaginatedResponse<Status>> {
    try {
      const url = getDataApiUrl(`${API_CONFIG.ENDPOINTS.STATUSES}?page=${page}&size=${size}`);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching statuses:', error);
      throw error;
    }
  }

  // Получение пользователей
  async getUsers(page: number = 0, size: number = 20): Promise<PaginatedResponse<User>> {
    try {
      const url = getDataApiUrl(`${API_CONFIG.ENDPOINTS.USERS}?page=${page}&size=${size}`);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  // Создание заявки
  async createRequest(requestData: CreateRequestData): Promise<any> {
    try {
      const url = getDataApiUrl(API_CONFIG.ENDPOINTS.REQUESTS);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response body:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating request:', error);
      throw error;
    }
  }

  // Получение заявок по ответственному пользователю
  async getRequestsByResponsible(userId: number, page: number = 0, size: number = 20): Promise<PaginatedResponse<any>> {
    try {
      const url = getDataApiUrl(`${API_CONFIG.ENDPOINTS.REQUESTS_BY_RESPONSIBLE}/${userId}?page=${page}&size=${size}`);
      const headers = this.getAuthHeaders();
      
      console.log('getRequestsByResponsible - URL:', url);
      console.log('getRequestsByResponsible - Headers:', headers);
      console.log('getRequestsByResponsible - User ID:', userId);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
      });

      console.log('getRequestsByResponsible - Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('getRequestsByResponsible - Error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('getRequestsByResponsible - Success response:', data);
      
      // API возвращает массив напрямую, а не объект с content
      if (Array.isArray(data)) {
        return {
          content: data,
          pageable: {
            pageNumber: page,
            pageSize: size,
            sort: { empty: false, sorted: false, unsorted: true },
            offset: page * size,
            paged: true,
            unpaged: false
          },
          last: true,
          totalPages: 1,
          totalElements: data.length,
          size: size,
          number: page,
          sort: { empty: false, sorted: false, unsorted: true },
          first: true,
          numberOfElements: data.length,
          empty: data.length === 0
        };
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching requests by responsible:', error);
      throw error;
    }
  }

  // Принятие задачи в работу
  async takeRequestInWork(requestId: number, userId: number): Promise<any> {
    try {
      const url = getDataApiUrl(`${API_CONFIG.ENDPOINTS.REQUESTS_TAKE_IN_WORK}/${requestId}/take-in-work?userId=${userId}`);
      const headers = this.getAuthHeaders();
      
      console.log('takeRequestInWork - URL:', url);
      console.log('takeRequestInWork - Headers:', headers);
      console.log('takeRequestInWork - Request ID:', requestId);
      console.log('takeRequestInWork - User ID:', userId);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
      });

      console.log('takeRequestInWork - Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('takeRequestInWork - Error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('takeRequestInWork - Success response:', data);
      return data;
    } catch (error) {
      console.error('Error taking request in work:', error);
      throw error;
    }
  }

  // Завершение задачи
  async completeRequest(requestId: number, userId: number): Promise<any> {
    try {
      const url = getDataApiUrl(`${API_CONFIG.ENDPOINTS.REQUESTS_COMPLETE}/${requestId}/complete?userId=${userId}`);
      const headers = this.getAuthHeaders();
      
      console.log('completeRequest - URL:', url);
      console.log('completeRequest - Headers:', headers);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
      });

      console.log('completeRequest - Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('completeRequest - Error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('completeRequest - Success response:', data);
      return data;
    } catch (error) {
      console.error('Error completing request:', error);
      throw error;
    }
  }

  // Получение активной задачи пользователя
  async getActiveRequest(userId: number): Promise<any> {
    try {
      const url = getDataApiUrl(`${API_CONFIG.ENDPOINTS.REQUESTS_ACTIVE}/${userId}/active`);
      const headers = this.getAuthHeaders();
      
      console.log('getActiveRequest - URL:', url);
      console.log('getActiveRequest - Headers:', headers);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
      });

      console.log('getActiveRequest - Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('getActiveRequest - Error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('getActiveRequest - Success response:', data);
      return data;
    } catch (error) {
      console.error('Error getting active request:', error);
      throw error;
    }
  }
}

const dataService = new DataService();
export default dataService; 